import React from 'react';
import { useClient } from '../../contexts/ClientContext';
import HomePageTemplate from './HomePageTemplate';

const CLIENT_STORAGE_KEY = 'client';

const getHomeFromStorage = () => {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem(CLIENT_STORAGE_KEY) : null;
    if (!raw) return null;
    const client = JSON.parse(raw);
    return client?.home ?? null;
  } catch (e) {
    return null;
  }
};

/**
 * Normalize client.home to { slug, page_type }. Supports dict (new) or string slug (legacy).
 */
const getHomeSlugAndType = (home) => {
  if (home == null) return { slug: '', pageType: 'landing' };
  if (typeof home === 'object' && home !== null) {
    const slug = typeof home.slug === 'string' ? home.slug.trim() : '';
    const pageType = home.page_type === 'webpage' ? 'webpage' : 'landing';
    return { slug, pageType };
  }
  if (typeof home === 'string') {
    return { slug: home.trim(), pageType: 'landing' };
  }
  return { slug: '', pageType: 'landing' };
};

/**
 * Domain check: main domain (127.0.0.1, localhost, seahire.co.in) vs partner portals.
 */
const MAIN_DOMAIN_HOSTS = ['127.0.0.1', 'localhost', 'seahire.co.in', 'www.seahire.co.in'];

const isMainDomain = () => {
  if (typeof window === 'undefined') return true;
  const hostname = (window.location.hostname || '').toLowerCase();
  return MAIN_DOMAIN_HOSTS.some((h) => hostname === h.toLowerCase());
};

/**
 * Renders "/" on main domain:
 * - If client.home (dict with slug + page_type, or legacy slug string) is set: render custom home template.
 * - Otherwise: render default HomePage.
 * On partner portals always renders PartnerPage.
 */
const HomePageOrPartnerPage = ({ HomePageComponent, PartnerPageComponent }) => {
  const client = useClient();
  const homeRaw = client?.home ?? getHomeFromStorage();
  const { slug: homeSlug, pageType } = getHomeSlugAndType(homeRaw);

  if (homeSlug) {
    return <HomePageTemplate slug={homeSlug} pageType={pageType} />;
  }

  if (!isMainDomain()) {
    return <PartnerPageComponent />;
  }

  return <HomePageComponent />;
};

export { getHomeFromStorage, getHomeSlugAndType };

export default HomePageOrPartnerPage;
